import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  MenuItem,
  Grid,
} from "@mui/material";
import PhoneInput from "react-phone-input-2";
import TopNavBar from "../components/TopNavBar";
import "react-phone-input-2/lib/material.css";
import countryList from "react-select-country-list";
import { createCustomer, getCustomerByName } from "../api/customer";

const countryOptions = countryList().getData();

function getCountryCode(countryLabel) {
  const found = countryOptions.find((option) => option.label === countryLabel);
  return found ? found.value.toLowerCase() : "dk";
}

export default function CustomerCreatePage() {
  const username = sessionStorage.getItem("username");
  const [form, setForm] = useState({
    customer_name: "",
    customer_address: "",
    customer_address_2: "",
    customer_city: "",
    customer_county: "",
    customer_postal_code: "",
    customer_country: "",
    customer_email: "",
    customer_telephone: "",
    customer_description: "",
    customer_phoneCountry: "dk",
  });
  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const navigate = useNavigate();

  // Check if all required fields are filled
  const isFormValid = useMemo(() => {
    const requiredFields = [
      "customer_name",
      "customer_address",
      "customer_city",
      "customer_county",
      "customer_postal_code",
      "customer_country",
      "customer_email",
      "customer_telephone",
    ];

    return requiredFields.every((field) => {
      const value = form[field];
      if (field === "customer_email" && value) {
        // Also check email format for validity
        return /\S+@\S+\.\S+/.test(value);
      }
      return value && value.trim() !== "";
    });
  }, [form]);

  const handleChange = (field) => (e) => {
    const updatedForm = { ...form, [field]: e.target.value };
    setForm(updatedForm);
    // Only validate this specific field if it's been touched
    if (touchedFields[field]) {
      validateField(field, updatedForm[field]);
    }
  };

  const handleCountryChange = (e) => {
    const countryLabel = e.target.value;
    const updatedForm = {
      ...form,
      customer_country: countryLabel,
      customer_phoneCountry: getCountryCode(countryLabel),
    };
    setForm(updatedForm);
    // Only validate this specific field if it's been touched
    if (touchedFields.customer_country) {
      validateField("customer_country", countryLabel);
    }
  };

  const handlePhoneChange = (value, data) => {
    const updatedForm = {
      ...form,
      customer_telephone: value,
      customer_phoneCountry: data.countryCode || form.customer_phoneCountry,
    };
    setForm(updatedForm);
    // Only validate this specific field if it's been touched
    if (touchedFields.customer_telephone) {
      validateField("customer_telephone", value);
    }
  };

  const handlePhoneBlur = () => {
    handleFieldBlur("customer_telephone")();
  };

  const handleFieldBlur = (field) => () => {
    // Mark field as touched and validate it
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
    validateField(field, form[field]);
  };

  const validateField = (field, value) => {
    const fieldErrors = { ...errors };

    // Remove existing error for this field
    delete fieldErrors[field];

    // Validate the specific field
    if (
      [
        "customer_name",
        "customer_address",
        "customer_city",
        "customer_county",
        "customer_postal_code",
        "customer_country",
        "customer_email",
        "customer_telephone",
      ].includes(field)
    ) {
      if (!value) {
        fieldErrors[field] = "Required";
      }
    }

    if (field === "customer_email" && value && !/\S+@\S+\.\S+/.test(value)) {
      fieldErrors.customer_email = "Invalid email";
    }

    setErrors(fieldErrors);
  };

  const validateForm = (formData = form) => {
    const newErrors = {};
    [
      "customer_name",
      "customer_address",
      "customer_city",
      "customer_county",
      "customer_postal_code",
      "customer_country",
      "customer_email",
      "customer_telephone",
    ].forEach((field) => {
      if (!formData[field]) newErrors[field] = "Required";
    });
    if (
      formData.customer_email &&
      !/\S+@\S+\.\S+/.test(formData.customer_email)
    )
      newErrors.customer_email = "Invalid email";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validate = () => {
    return validateForm();
  };

  // Add this function to check if a customer name exists
  async function checkCustomerNameExists(name) {
    if (!name) return false;
    try {
      const response = await getCustomerByName(name);
      return response.exists;
    } catch (err) {
      // Optionally handle error (e.g., network issue)
      return false;
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const nameExists = await checkCustomerNameExists(form.customer_name);
    if (nameExists) {
      setErrors({ customer_name: "Customer with this name already exists" });
      return;
    }
    const result = await createCustomer(form);
    if (result && result.success) {
      alert("Customer created successfully");
      navigate(`/customer/${result.customer.pk_id}`);
      setForm({
        customer_name: "",
        customer_address: "",
        customer_address_2: "",
        customer_city: "",
        customer_county: "",
        customer_postal_code: "",
        customer_country: "",
        customer_email: "",
        customer_telephone: "",
        customer_description: "",
        customer_phoneCountry: "dk",
      });
    }
  };

  return (
    <>
      <TopNavBar
        title="Robot Cleaner Dashboard"
        username={username}
        heading={"Customer Creation"}
      />
      <Container
        sx={{
          mt: 4,
          width: { xs: "90%", sm: "70%", md: "50%", lg: "40%" }, // xs <600px => 90%
          maxWidth: "none",
          mx: "auto",
        }}
      >
        <Typography variant="h5" sx={{ mb: 3 }}>
          Create New Customer
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label="Name"
            fullWidth
            required
            margin="normal"
            value={form.customer_name}
            onChange={handleChange("customer_name")}
            onBlur={handleFieldBlur("customer_name")}
            error={!!errors.customer_name}
            helperText={errors.customer_name}
          />
          <TextField
            label="Address"
            fullWidth
            required
            margin="normal"
            value={form.customer_address}
            onChange={handleChange("customer_address")}
            onBlur={handleFieldBlur("customer_address")}
            error={!!errors.customer_address}
            helperText={errors.customer_address}
          />
          <TextField
            label="Address Line 2 (Optional)"
            fullWidth
            margin="normal"
            value={form.customer_address_2}
            onChange={handleChange("customer_address_2")}
            placeholder="Apartment, suite, unit, building, floor, etc."
          />
          <Grid container spacing={{ sm: 2, xs: 0 }} columns={12}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="City"
                fullWidth
                required
                margin="normal"
                value={form.customer_city}
                onChange={handleChange("customer_city")}
                onBlur={handleFieldBlur("customer_city")}
                error={!!errors.customer_city}
                helperText={errors.customer_city}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="County"
                fullWidth
                required
                margin="normal"
                value={form.customer_county}
                onChange={handleChange("customer_county")}
                onBlur={handleFieldBlur("customer_county")}
                error={!!errors.customer_county}
                helperText={errors.customer_county}
              />
            </Grid>
          </Grid>
          <Grid container spacing={{ sm: 2, xs: 0 }} columns={12}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Postal Code"
                fullWidth
                required
                margin="normal"
                value={form.customer_postal_code}
                onChange={handleChange("customer_postal_code")}
                onBlur={handleFieldBlur("customer_postal_code")}
                error={!!errors.customer_postal_code}
                helperText={errors.customer_postal_code}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Country"
                select
                fullWidth
                required
                margin="normal"
                value={form.customer_country ?? ""}
                onChange={handleCountryChange}
                onBlur={handleFieldBlur("customer_country")}
                error={!!errors.customer_country}
                helperText={errors.customer_country}
                SelectProps={{
                  MenuProps: { PaperProps: { style: { maxHeight: 300 } } },
                }}
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {countryOptions.map((option) => (
                  <MenuItem key={option.value} value={option.label}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          <Grid container spacing={{ sm: 2, xs: 0 }} columns={12}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Email"
                fullWidth
                required
                margin="normal"
                value={form.customer_email ?? ""}
                onChange={handleChange("customer_email")}
                onBlur={handleFieldBlur("customer_email")}
                error={!!errors.customer_email}
                helperText={errors.customer_email}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ mt: 2 }}>
                <PhoneInput
                  country={form.customer_phoneCountry ?? "dk"}
                  value={form.customer_telephone ?? ""}
                  onChange={handlePhoneChange}
                  onBlur={handlePhoneBlur}
                  inputStyle={{ width: "100%" }}
                  specialLabel="Telephone"
                  inputProps={{
                    required: true,
                    name: "telephone",
                  }}
                  containerStyle={{ width: "100%" }}
                  isValid={(value, country) => !!value}
                />
                {errors.customer_telephone && (
                  <Typography color="error" variant="caption">
                    {errors.customer_telephone}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            minRows={2}
            value={form.customer_description}
            onChange={handleChange("customer_description")}
          />
          <Button
            type="submit"
            disabled={!isFormValid}
            variant="contained"
            color="primary"
            sx={{ mt: 2, mb: 4, display: "block", mx: "auto" }}
          >
            Create Customer
          </Button>
        </Box>
      </Container>
    </>
  );
}
